import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, Copy, QrCode, Search, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { formatRupiah } from "../lib/format";
import { AmountInput } from "../components/AmountInput";
import { PinDialog } from "../components/PinDialog";

export default function Qris() {
  const navigate = useNavigate();
  // Terima
  const [genAmount, setGenAmount] = useState(0);
  const [genNote, setGenNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [qris, setQris] = useState(null);
  // Bayar
  const [payload, setPayload] = useState("");
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [pinOpen, setPinOpen] = useState(false);
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/qris/generate", {
        amount: genAmount > 0 ? genAmount : null,
        note: genNote,
      });
      setQris(res.data);
      toast.success("Kode QRIS berhasil dibuat!");
    } catch (err) {
      toast.error(errMsg(err, "Gagal membuat QRIS"));
    } finally {
      setGenerating(false);
    }
  };

  const copyPayload = (p) => {
    navigator.clipboard.writeText(p);
    toast.success("Kode QRIS disalin");
  };

  const parse = async () => {
    if (!payload.trim()) return;
    setParsing(true);
    setParsed(null);
    try {
      const res = await api.post("/qris/parse", { payload: payload.trim() });
      if (res.data.is_own) {
        toast.error("Ini QRIS milikmu sendiri — tidak bisa dibayar");
        return;
      }
      if (res.data.status !== "ACTIVE") {
        toast.error("QRIS ini sudah tidak aktif / sudah dibayar");
        return;
      }
      setParsed(res.data);
      setIdemKey(crypto.randomUUID());
    } catch (err) {
      toast.error(errMsg(err, "Kode QRIS tidak valid"));
    } finally {
      setParsing(false);
    }
  };

  const openPay = () => {
    const amount = parsed?.amount || payAmount;
    if (!amount || amount < 1000) {
      toast.error("Masukkan nominal minimal Rp 1.000");
      return;
    }
    setPinOpen(true);
  };

  const confirmPay = async (pin) => {
    const res = await api.post("/qris/pay", {
      payload: payload.trim(),
      amount: parsed?.amount ? null : payAmount,
      pin,
      idempotency_key: idemKey,
    });
    setPinOpen(false);
    toast.success("Pembayaran QRIS berhasil!");
    navigate(`/transaksi/${res.data.id}`);
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-semibold">QRIS</h1>
        <p className="text-sm text-muted-foreground">Terima dan bayar dengan satu kode QR standar nasional.</p>
      </div>

      <Tabs defaultValue="terima">
        <TabsList className="grid grid-cols-2 w-full h-11 rounded-xl">
          <TabsTrigger value="terima" className="rounded-lg" data-testid="qris-tab-terima">Terima</TabsTrigger>
          <TabsTrigger value="bayar" className="rounded-lg" data-testid="qris-tab-bayar">Bayar</TabsTrigger>
        </TabsList>

        <TabsContent value="terima" className="mt-4 space-y-4">
          {!qris ? (
            <Card className="rounded-2xl">
              <CardContent className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <Label>Nominal (kosongkan untuk nominal bebas)</Label>
                  <AmountInput value={genAmount} onChange={setGenAmount} testId="qris-generate-amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Catatan (opsional)</Label>
                  <Input data-testid="qris-generate-note-input" className="h-11" placeholder="Contoh: bayar kopi" value={genNote} onChange={(e) => setGenNote(e.target.value)} maxLength={40} />
                </div>
                <Button className="w-full h-12 text-base" onClick={generate} disabled={generating} data-testid="qris-generate-button">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><QrCode className="h-4 w-4 mr-1.5" /> Buat Kode QRIS</>)}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
              <Card className="rounded-2xl border-primary/30">
                <CardContent className="p-5 space-y-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <h3 className="font-heading font-semibold">{qris.merchant_name}</h3>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-100">QRIS Aktif</Badge>
                  </div>
                  <div className="mx-auto w-fit rounded-2xl border bg-white p-3 relative">
                    <span className="absolute top-0 left-0 h-6 w-6 border-t-4 border-l-4 border-[#1FC8A9] rounded-tl-2xl" />
                    <img src={qris.qr_image} alt="Kode QRIS" className="h-56 w-56" data-testid="qris-qr-image" />
                  </div>
                  <p className="font-heading text-xl font-bold tabular-nums" data-testid="qris-generated-amount">
                    {qris.amount ? formatRupiah(qris.amount) : "Nominal bebas"}
                  </p>
                  {qris.note && <p className="text-sm text-muted-foreground">{qris.note}</p>}
                  <div className="rounded-xl bg-secondary p-3 text-left">
                    <p className="text-xs text-muted-foreground mb-1">Kode QRIS (untuk dibayar via tab Bayar)</p>
                    <div className="flex items-start gap-2">
                      <p className="font-mono text-[10px] break-all flex-1" data-testid="qris-payload-text">{qris.payload}</p>
                      <Button size="sm" variant="secondary" onClick={() => copyPayload(qris.payload)} data-testid="qris-copy-payload-button" aria-label="Salin kode QRIS">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <Button variant="ghost" className="w-full" onClick={() => { setQris(null); setGenAmount(0); setGenNote(""); }} data-testid="qris-create-another-button">
                    Buat QRIS Baru
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </TabsContent>

        <TabsContent value="bayar" className="mt-4 space-y-4">
          <Card className="rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <div className="space-y-1.5">
                <Label>Tempel Kode QRIS</Label>
                <Textarea
                  data-testid="qris-pay-payload-input"
                  placeholder="Tempel (paste) kode QRIS di sini..."
                  className="font-mono text-xs min-h-24"
                  value={payload}
                  onChange={(e) => { setPayload(e.target.value); setParsed(null); }}
                />
                <p className="text-xs text-muted-foreground">Salin kode dari QRIS penerima, lalu tempel di sini untuk membayar.</p>
              </div>
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5" onClick={parse} disabled={parsing || !payload.trim()} data-testid="qris-check-button">
                {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Search className="h-4 w-4 mr-1.5" /> Cek QRIS</>)}
              </Button>
              {parsed && (
                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="qris-parsed-card">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                    <div>
                      <p className="font-semibold text-sm" data-testid="qris-merchant-name">{parsed.merchant_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {parsed.amount ? formatRupiah(parsed.amount) : "Nominal bebas"}{parsed.note ? ` · ${parsed.note}` : ""}
                      </p>
                    </div>
                  </div>
                  {!parsed.amount && (
                    <div className="space-y-1.5">
                      <Label>Nominal Pembayaran</Label>
                      <AmountInput value={payAmount} onChange={setPayAmount} testId="qris-pay-amount-input" />
                    </div>
                  )}
                  <Button className="w-full h-12 text-base bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90" onClick={openPay} data-testid="qris-pay-button">
                    Bayar {parsed.amount ? formatRupiah(parsed.amount) : payAmount > 0 ? formatRupiah(payAmount) : ""}
                  </Button>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={confirmPay} />
    </div>
  );
}
